import { BaseService, PagedResult } from '../base/base.service';
import { PermissionProviderType } from './role.types';
import { resolveRuntimeApiGatewayUrl } from '@/config/runtime-api';
import type {
  Role,
  CreateRoleRequest,
  UpdateRoleRequest,
  RoleQueryParams,
  RolePermissionsResponse,
  UpdatePermissionsRequest,
} from './role.types';

/**
 * Role Service
 * Provides role management functionality for admin panel
 * Integrates with ABP Identity Service APIs
 *
 * Features:
 * - Role CRUD operations
 * - Permission assignment and management
 * - Role-permission matrix operations
 * - Integration with ABP Identity module
 *
 * API Endpoints:
 * - GET /api/v1/identity/roles - List roles
 * - GET /api/v1/identity/roles/{id} - Get role details
 * - POST /api/v1/identity/roles - Create role
 * - PUT /api/v1/identity/roles/{id} - Update role
 * - DELETE /api/v1/identity/roles/{id} - Delete role
 * - GET /api/permission-management/permissions - Get role permissions
 * - PUT /api/permission-management/permissions - Update permissions
 */
export class RoleService extends BaseService {
  constructor() {
    super('/api/identity/roles');
  }

  /**
   * Get paginated list of roles with filtering and sorting
   *
   * @param params - Query parameters (filter, sorting, pagination)
   * @returns Paged result of roles
   *
   * @example
   * ```ts
   * const roles = await roleService.getRoles({
   *   filter: 'admin',
   *   sorting: 'name asc',
   *   skipCount: 0,
   *   maxResultCount: 10
   * });
   * ```
   */
  async getRoles(params: RoleQueryParams = {}): Promise<PagedResult<Role>> {
    const queryParams = this.buildRoleQueryParams(params);
    return this.get<PagedResult<Role>>('', { params: queryParams });
  }

  /**
   * Get single role by ID
   *
   * @param id - Role ID
   * @returns Role details
   *
   * @example
   * ```ts
   * const role = await roleService.getRole('role-admin-123');
   * ```
   */
  async getRole(id: string): Promise<Role> {
    this.validateId(id, 'Role ID');
    return this.get<Role>(`/${id}`);
  }

  /**
   * Get role by name
   *
   * @param name - Role name
   * @returns Role details or null if not found
   *
   * @example
   * ```ts
   * const adminRole = await roleService.getRoleByName('Admin');
   * ```
   */
  async getRoleByName(name: string): Promise<Role | null> {
    if (!name || name.trim().length === 0) {
      throw new Error('Role name is required');
    }

    const result = await this.getRoles({ filter: name, maxResultCount: 1 });
    const exactMatch = result.items.find(
      (role) => role.name.toLowerCase() === name.toLowerCase()
    );

    return exactMatch || null;
  }

  /**
   * Create new role
   *
   * @param data - Role creation data
   * @returns Created role
   *
   * @example
   * ```ts
   * const role = await roleService.createRole({
   *   name: 'Moderator',
   *   isDefault: false,
   *   isPublic: true,
   *   extraProperties: { description: 'Content moderation access' }
   * });
   * ```
   */
  async createRole(data: CreateRoleRequest): Promise<Role> {
    const validated = this.validateCreateRoleRequest(data);
    return this.post<Role, CreateRoleRequest>('', validated);
  }

  /**
   * Update existing role
   *
   * @param id - Role ID
   * @param data - Role update data
   * @returns Updated role
   *
   * @example
   * ```ts
   * const role = await roleService.updateRole('role-moderator-789', {
   *   name: 'Senior Moderator',
   *   isDefault: false,
   *   isPublic: true,
   *   concurrencyStamp: 'abc123',
   *   extraProperties: { description: 'Senior moderation access' }
   * });
   * ```
   */
  async updateRole(id: string, data: UpdateRoleRequest): Promise<Role> {
    this.validateId(id, 'Role ID');
    this.validateUpdateRoleRequest(data);
    return this.put<Role, UpdateRoleRequest>(`/${id}`, data);
  }

  /**
   * Delete role
   * Note: Cannot delete static roles (like Admin)
   *
   * @param id - Role ID
   *
   * @example
   * ```ts
   * await roleService.deleteRole('role-moderator-789');
   * ```
   *
   * @throws {ApiError} 400 if trying to delete a static role
   */
  async deleteRole(id: string): Promise<void> {
    this.validateId(id, 'Role ID');
    return this.delete<void>(`/${id}`);
  }

  /**
   * Get permissions assigned to a role
   *
   * @param roleName - Role name (not ID - ABP uses name as provider key)
   * @returns Role permissions grouped by provider
   *
   * @example
   * ```ts
   * const permissions = await roleService.getRolePermissions('Admin');
   * ```
   */
  async getRolePermissions(roleName: string): Promise<RolePermissionsResponse> {
    if (!roleName || roleName.trim().length === 0) {
      throw new Error('Role name is required');
    }

    const baseUrl = resolveRuntimeApiGatewayUrl();
    const url = `${baseUrl}/api/permission-management/permissions`;

    const params = {
      providerName: PermissionProviderType.Role,
      providerKey: roleName,
    };

    return this.handleRequest(
      this.api.get<RolePermissionsResponse>(url, { params })
    );
  }

  /**
   * Update permissions for a role
   *
   * @param roleName - Role name (not ID)
   * @param permissions - Array of permissions with grant status
   *
   * @example
   * ```ts
   * await roleService.updateRolePermissions('Moderator', {
   *   permissions: [
   *     { name: 'Identity.Users.Read', isGranted: true },
   *     { name: 'Identity.Users.Create', isGranted: false },
   *     { name: 'Content.Posts.Update', isGranted: true }
   *   ]
   * });
   * ```
   */
  async updateRolePermissions(
    roleName: string,
    permissions: UpdatePermissionsRequest
  ): Promise<void> {
    if (!roleName || roleName.trim().length === 0) {
      throw new Error('Role name is required');
    }

    if (!permissions.permissions || permissions.permissions.length === 0) {
      throw new Error('Permissions array cannot be empty');
    }

    const baseUrl = resolveRuntimeApiGatewayUrl();
    const identityUrl = `${baseUrl}/api/permission-management/permissions`;
    const params = {
      providerName: PermissionProviderType.Role,
      providerKey: roleName,
    };

    await this.handleRequest(
      this.api.put<void>(identityUrl, permissions, { params })
    );
  }

  /**
   * Get users assigned to a role
   *
   * @param roleId - Role ID
   * @param params - Query parameters for pagination
   * @returns User count and list (delegates to UserService)
   *
   * Note: This is a convenience method that uses the UserService
   * For full user management, use UserService directly
   */
  async getRoleUsers(
    roleId: string,
    params: { skipCount?: number; maxResultCount?: number } = {}
  ): Promise<number> {
    this.validateId(roleId, 'Role ID');

    // This calls the users endpoint with role filter
    const baseUrl = resolveRuntimeApiGatewayUrl();
    const url = `${baseUrl}/api/identity/users`;

    const queryParams = {
      roleId,
      skipCount: params.skipCount || 0,
      maxResultCount: params.maxResultCount || 1,
    };

    const result = await this.handleRequest(
      this.api.get<PagedResult<Record<string, unknown>>>(url, { params: queryParams })
    );

    return result.totalCount;
  }

  /**
   * Build query parameters for role list request
   */
  private buildRoleQueryParams(
    params: RoleQueryParams
  ): Record<string, string | number> {
    const queryParams: Record<string, string | number> = {};

    if (params.filter) queryParams.Filter = params.filter;
    if (params.sorting) queryParams.Sorting = params.sorting;
    if (params.skipCount !== undefined) queryParams.SkipCount = params.skipCount;
    if (params.maxResultCount !== undefined)
      queryParams.MaxResultCount = params.maxResultCount;

    return queryParams;
  }

  /**
   * Validate role ID
   */
  private validateId(id: string, fieldName: string = 'ID'): void {
    if (!id || id.trim().length === 0) {
      throw new Error(`${fieldName} is required`);
    }
  }

  /**
   * Validate create role request
   */
  private validateCreateRoleRequest(data: CreateRoleRequest): CreateRoleRequest {
    if (!data.name || data.name.trim().length === 0) {
      throw new Error('Role name is required');
    }

    if (data.name.length > 256) {
      throw new Error('Role name cannot exceed 256 characters');
    }

    // Role name should not contain special characters
    const nameRegex = /^[a-zA-Z0-9_\-\s.]+$/;
    if (!nameRegex.test(data.name)) {
      throw new Error(
        'Role name can only contain letters, numbers, spaces, hyphens, underscores, and dots'
      );
    }

    return data;
  }

  /**
   * Validate update role request
   */
  private validateUpdateRoleRequest(data: UpdateRoleRequest): void {
    if (!data.name || data.name.trim().length === 0) {
      throw new Error('Role name is required');
    }

    if (!data.concurrencyStamp) {
      throw new Error('Concurrency stamp is required for updates');
    }

    if (data.name.length > 256) {
      throw new Error('Role name cannot exceed 256 characters');
    }

    const nameRegex = /^[a-zA-Z0-9_\-\s.]+$/;
    if (!nameRegex.test(data.name)) {
      throw new Error(
        'Role name can only contain letters, numbers, spaces, hyphens, underscores, and dots'
      );
    }
  }
}

// Export singleton instance
export const roleService = new RoleService();
